<template>
    <div class="audio-settings">
        <div class="header-section">
            <h2 class="section-title">System Recordings</h2>
            <div class="search-container">
                <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Search" outlined dense
                    hide-details class="search-field" bg-color="white"></v-text-field>
            </div>
        </div>

        <div class="table-container">
            <v-data-table :headers="headers" :items="systemRecords" :search="search" :footer-props="{
                'items-per-page-options': [50, 100, 500],
                'items-per-page-text': 'Rows per page:'
            }" class="audio-table elevation-1" :loading="progressShow" loading-text="Loading data...">

                <template v-if="$vuetify.breakpoint.mdAndUp" v-for="header in headers"
                    v-slot:[`header.${header.value}`]="{ header }">
                    <span class="table-headers-span-settings">{{ header.text }}</span>
                </template>
                <template v-slot:item="{ item, index }">
                    <tr class="table-row">
                        <td class="text-center">{{ item.fileName }}</td>

                        <td class="text-center">
                            <div class="audio-controls">
                                <v-btn v-if="item.playingLanguage === 'he'" icon small color="#0d2c6d"
                                    @click="stop(item)" class="control-btn">
                                    <v-icon>mdi-stop</v-icon>
                                </v-btn>
                                <v-btn v-else icon small color="#0d2c6d" @click="play(item, 'he')" class="control-btn">
                                    <v-icon>mdi-play</v-icon>
                                </v-btn>
                            </div>
                        </td>

                        <td class="text-center">
                            <div class="audio-controls">
                                <v-btn v-if="item.playingLanguage === 'en'" icon small color="#0d2c6d"
                                    @click="stop(item)" class="control-btn">
                                    <v-icon>mdi-stop</v-icon>
                                </v-btn>
                                <v-btn v-else icon small color="#0d2c6d" @click="play(item, 'en')" class="control-btn">
                                    <v-icon>mdi-play</v-icon>
                                </v-btn>
                            </div>
                        </td>

                        <td class="text-center">
                            <div class="audio-controls">
                                <v-btn v-if="item.playingLanguage === 'yi_il'" icon small color="#0d2c6d"
                                    @click="stop(item)" class="control-btn">
                                    <v-icon>mdi-stop</v-icon>
                                </v-btn>
                                <v-btn v-else icon small color="#0d2c6d" @click="play(item, 'yi_il')"
                                    class="control-btn">
                                    <v-icon>mdi-play</v-icon>
                                </v-btn>
                            </div>
                        </td>

                        <td class="text-center">
                            <div class="audio-controls">
                                <v-btn v-if="item.playingLanguage === 'yi_us'" icon small color="#0d2c6d"
                                    @click="stop(item)" class="control-btn">
                                    <v-icon>mdi-stop</v-icon>
                                </v-btn>
                                <v-btn v-else icon small color="#0d2c6d" @click="play(item, 'yi_us')"
                                    class="control-btn">
                                    <v-icon>mdi-play</v-icon>
                                </v-btn>
                            </div>
                        </td>

                        <td class="text-center">
                            <div class="audio-controls">
                                <v-btn v-if="item.playingLanguage === 'custom'" icon small color="#0d2c6d"
                                    @click="stop(item)" class="control-btn">
                                    <v-icon>mdi-stop</v-icon>
                                </v-btn>
                                <v-btn v-else icon small color="#0d2c6d" @click="play(item, null)" class="control-btn">
                                    <v-icon>mdi-play</v-icon>
                                </v-btn>

                                <v-btn icon small color="#0d2c6d" class="control-btn ml-2"
                                    @click="triggerFileInput(index)">
                                    <v-icon>mdi-upload</v-icon>
                                </v-btn>
                                <input type="file" :id="'fileInput' + index" ref="fileInputs"
                                    @change="uploadFile(item, $event)" style="display: none" accept="audio/*" />

                                <v-btn v-if="item.costumeExist" icon small color="#0d2c6d" class="control-btn ml-2"
                                    @click="confirmDelete(item)">
                                    <v-icon>mdi-delete</v-icon>
                                </v-btn>
                            </div>
                        </td>

                        <td class="text-center file-content">{{ item.fileContent }}</td>
                    </tr>
                </template>

                <template v-slot:no-data>
                    <div class="empty-state">
                        <v-icon size="64" color="grey lighten-1">mdi-music-off</v-icon>
                        <p class="empty-text">No system recordings available</p>
                        <v-btn color="#0d2c6d" dark @click="refreshDetails" class="mt-4">
                            <v-icon left>mdi-refresh</v-icon>
                            Refresh
                        </v-btn>
                    </div>
                </template>
            </v-data-table>
        </div>

        <v-overlay :value="overlay" :opacity="0.8">
            <v-card class="loading-card pa-4" elevation="8">
                <v-progress-circular indeterminate size="64" color="#0d2c6d" class="mb-4"></v-progress-circular>
                <span class="loading-text">{{ loadingText }}</span>
            </v-card>
        </v-overlay>

        <v-dialog v-model="deleteDialog" max-width="400">
            <v-card>
                <v-card-title class="headline">Delete Recording</v-card-title>
                <v-card-text>
                    Are you sure you want to delete the custom recording?
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey darken-1" text @click="deleteDialog = false">
                        Cancel
                    </v-btn>
                    <v-btn color="#0d2c6d" dark @click="deleteConfirmed">
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar v-model="snackbar" :color="snackbarColorBt" :timeout="4000" bottom right>
            {{ snacbarText }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar = false">
                    Close
                </v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import ApiServices from '@/services/api.service'
import Auth from '@/services/auth.service.js'
import axios from 'axios'

export default {
    name: 'AudioSettings',
    data: () => ({
        snackbar: false,
        snackbarColorBt: "success",
        snacbarText: "",
        lineNumber: "",
        progressShow: false,
        deleteDialog: false,
        itemToDelete: null,
        search: "",
        systemRecords: [],
        overlay: false,
        loadingText: "Loading...",
        headers: [
            { text: 'Message Code', align: 'center', value: 'fileName', width: '15%' },
            { text: 'Hebrew', value: 'he', align: 'center', width: '10%' },
            { text: 'English', value: 'en', align: 'center', width: '10%' },
            { text: 'Yiddish (IL)', value: 'yi_il', align: 'center', width: '10%' },
            { text: 'Yiddish (US)', value: 'yi_us', align: 'center', width: '10%' },
            { text: 'Custom', value: 'costume', align: 'center', width: '15%' },
            { text: 'Recording Content', value: 'fileContent', align: 'center' },
        ],
    }),
    methods: {
        async getSystemRecords() {
            try {
                this.progressShow = true;
                let token = localStorage.getItem("token");
                let api = process.env.VUE_APP_BASE_URL + `/settings/get_system_records`;
                const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
                const jsonObject = await res.json();
                this.progressShow = false;

                if (res.status >= 400) {
                    this.showSnackBar("Error fetching data", "error");
                } else if (res.status === 200) {
                    // Add playingLanguage property to each record
                    this.systemRecords = jsonObject.map(record => ({
                        ...record,
                        playingLanguage: null
                    }));
                }
            } catch (error) {
                this.progressShow = false;
                this.showSnackBar("Error loading data: " + error, "error");
            }
        },

        async setAudio(record, language) {
            try {
                this.loadingText = "Loading recording...";
                this.overlay = true;

                let token = localStorage.getItem("token");
                let api;
                const firstChar = record.fileName.charAt(0);

                if (record.costumeExist && !language) {
                    api = process.env.VUE_APP_BASE_URL + `/recordings/costumFile/${this.lineNumber}/${firstChar}/` + record.fileName;
                } else {
                    api = process.env.VUE_APP_BASE_URL + `/recordings/systemFile/${language}/${firstChar}/` + record.fileName;
                }

                const res = await fetch(api, ApiServices.requestOptions("GET", "", token));

                if (res.status >= 400) {
                    this.showSnackBar("Error loading recording", "error");
                    record.record_link = "";
                    record.record_audio = "";
                    this.overlay = false;
                    return false;
                } else if (res.status === 200) {
                    const jsonObject = await res.blob();
                    const url = URL.createObjectURL(jsonObject);
                    record.record_link = url;
                    record.record_audio = new Audio(url);
                    this.overlay = false;
                    return true;
                }
            } catch (error) {
                this.showSnackBar("Error loading recording: " + error, "error");
                this.overlay = false;
                return false;
            }
        },

        showSnackBar(message, color) {
            this.snacbarText = message;
            this.snackbarColorBt = color === "error" ? "red" : "green";
            this.snackbar = true;
        },

        async play(item, lang) {
            // Stop any currently playing audio
            this.systemRecords.forEach(record => {
                if (record.playingLanguage && record !== item) {
                    this.stop(record);
                }
            });

            const success = await this.setAudio(item, lang);

            if (success && item.record_audio) {
                // Set the playing language to show the stop button
                item.playingLanguage = lang || 'custom';

                item.record_audio.play();

                item.record_audio.addEventListener('ended', () => {
                    this.stop(item);
                });

                item.record_audio.addEventListener('error', () => {
                    this.stop(item);
                    this.showSnackBar('Error: Invalid audio file', "error");
                });
            }
        },

        stop(item) {
            if (item.record_audio) {
                item.record_audio.pause();
                item.record_audio.currentTime = 0;
                item.playingLanguage = null;
            }
        },

        triggerFileInput(index) {
            this.$nextTick(() => {
                document.getElementById('fileInput' + index).click();
            });
        },

        async uploadFile(item, event) {
            const file = event.target.files[0];
            if (!file) return;

            try {
                this.loadingText = "Uploading file...";
                this.overlay = true;

                let token = localStorage.getItem("token");
                let api = process.env.VUE_APP_BASE_URL + `/settings/change_system_record`;

                const formData = new FormData();
                formData.append("record", file);
                formData.append("recordName", item.fileName);

                const response = await axios.post(api, formData, {
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'multipart/form-data'
                    }
                });

                if (response.status >= 400) {
                    this.showSnackBar("Error: Cannot upload file", "error");
                } else if (response.status === 200) {
                    this.showSnackBar("File uploaded successfully!", "success");
                    const index = this.systemRecords.findIndex(record => record.id === item.id);
                    this.systemRecords[index].costumeExist = true;
                }
            } catch (error) {
                this.showSnackBar("Error uploading file: " + error, "error");
            } finally {
                this.overlay = false;
                // Reset file input
                event.target.value = '';
            }
        },

        confirmDelete(item) {
            this.itemToDelete = item;
            this.deleteDialog = true;
        },

        async deleteConfirmed() {
            if (!this.itemToDelete) return;

            try {
                this.deleteDialog = false;
                this.loadingText = "Deleting recording...";
                this.overlay = true;

                let token = localStorage.getItem("token");
                const recordJSON = JSON.stringify({ item: this.itemToDelete });
                let api = process.env.VUE_APP_BASE_URL + `/settings/delete_system_records`;

                const res = await fetch(api, ApiServices.requestOptions("DELETE", recordJSON, token));

                if (res.status >= 400) {
                    this.showSnackBar("Error deleting file", "error");
                } else if (res.status === 200) {
                    this.showSnackBar("Recording deleted successfully!", "success");
                    const index = this.systemRecords.findIndex(record => record.id === this.itemToDelete.id);
                    this.systemRecords[index].costumeExist = false;
                }
            } catch (error) {
                this.showSnackBar("Error deleting file: " + error, "error");
            } finally {
                this.overlay = false;
                this.itemToDelete = null;
            }
        },

        refreshDetails() {
            this.getSystemRecords();
        }
    },
    mounted() {
        this.lineNumber = Auth.getLineNumber();
        this.getSystemRecords();
    },
    beforeDestroy() {
        // Clean up any playing audio
        this.systemRecords.forEach(item => {
            if (item.record_audio) {
                item.record_audio.pause();
                item.record_audio = null;
            }
        });
    }
}
</script>

<style scoped>
.audio-settings {
    padding: 3rem;
    background-color: #f5f7fa;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
}

.section-title {
    color: #183677;
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
}

.search-container {
    width: 300px;
    max-width: 100%;
}

.search-field {
    border-radius: 8px;
}

.table-container {
    background-color: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.audio-table {
    border-radius: 8px;
}

/* Style for table headers */
.audio-table>>>th {
    background-color: rgba(0, 0, 0, 0.116) !important;
    height: 40px !important;
    text-align: center !important;
    font-family: 'Heebo' !important;
    font-size: 15px;
    font-weight: 600 !important;
    color: #0d2c6d;
}

.table-row {
    transition: background-color 0.2s;
    height: 49px !important;
    box-shadow: -1.8369701465288538e-16px 3px 10px rgba(119, 119, 119, 0.1) !important;
    border-radius: 3px !important;
}

.table-row:hover {
    background-color: #f8f9fd !important;
}

.audio-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
}

.control-btn {
    margin: 0 2px;
}

.file-content {
    font-family: 'Heebo' !important;
    font-size: 17px !important;
    color: #47484b;
    text-align: center !important;
    white-space: normal;
    word-break: break-word;
}

/* Alternating row colors */
.audio-table>>>tbody tr:nth-child(even) {
    background-color: rgba(0, 0, 0, 0.05);
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 1rem;
}

.empty-text {
    margin-top: 1rem;
    font-size: 1.2rem;
    color: #666;
}

.loading-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: white;
    border-radius: 8px;
}

.loading-text {
    font-size: 1rem;
    color: #333;
}

/* Responsive adjustments */
@media (max-width: 960px) {
    .header-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }

    .search-container {
        width: 100%;
    }
}

@media (max-width: 600px) {
    .audio-settings {
        padding: 0.5rem;
    }

    .section-title {
        font-size: 1.25rem;
    }
}

.table-headers-span-settings {
    font-family: 'Heebo' !important;
    font-size: 15px;
    font-weight: 600 !important;
    color: #0d2c6d;
}
</style>